import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { AMBASSADOR_SOURCE_LOCATION } from '#src/app/shared/constants';
import { normalize } from '#src/app/shared/utils/string';

import type { PublishedPetitionResponse } from './api/createPublishedPetition';
import { fetchSocialStarterAngleSourceLocation } from './api/socialStarterAngleSourceLocation';

// helper function to check if AI draft has been edited
// this information is passed in the activate_petition tracking event
export function compareNormalizedStrings(base: string | undefined | null, value: string): boolean | null {
	if (base === undefined || base === null) return null;

	return normalize(base) !== normalize(value);
}

/**
 * Resolves the `source_location` to publish with. For social starter drafts (identified by the
 * `angleToken` persisted on the draft) the draft angle's `metadata.source_location` is the source of
 * truth for tagging — we read it fresh at publish and use it for either code (ambassador or plain
 * social), immune to the lossy `?source_location` URL param that could be dropped on a bookmark or
 * history return. We fall back to the URL value only when the draft has no stamped code yet (older
 * drafts predating the metadata tag) or the lookup fails. Non-social-starter drafts (no angleToken)
 * keep the URL value unchanged — `source_location` there is general attribution, not our concern.
 */
export async function resolvePublishSourceLocation({
	angleToken,
	urlSourceLocation,
	utilityContext,
}: {
	angleToken: string | null | undefined;
	urlSourceLocation: string | undefined;
	utilityContext: UtilityContext;
}): Promise<string | undefined> {
	if (!angleToken) return urlSourceLocation;

	const sourceLocation = await fetchSocialStarterAngleSourceLocation({ token: angleToken, utilityContext });
	// A blank stamped code shouldn't happen, but guard it the same as a missing one — the petition
	// service validates source_location against a fixed set, so an empty string would 400 rather
	// than silently falling back to the URL value.
	return sourceLocation || urlSourceLocation;
}

// Branches on the resolved `source_location` rather than the petition's `isAmbassadorPetition` field:
// staffing is applied asynchronously, so that field can still read false here — and that false would
// then be cached for 30 minutes.
export function resolvePostPublishPath({
	petition,
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
}: {
	petition: Pick<PublishedPetitionResponse, 'id' | 'slug'>;
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
}): string {
	const petitionPath = `/p/${petition.slug ?? petition.id}`;
	const isAmbassadorPetition = resolvedSourceLocation === AMBASSADOR_SOURCE_LOCATION;

	if (isAmbassadorPostCreateFlowEnabled && isAmbassadorPetition) return `${petitionPath}/ambassador-onboarding`;

	return `${petitionPath}/starter-onboarding/welcome`;
}
